/**
 * Message Adapter - Transform AI SDK v6 messages to backend schema
 *
 * Converts AI SDK v6 format (messages with parts) to backend /ai/chat-v2 schema:
 * - Extracts text content from parts array
 * - Converts tool parts with approval states into toolInvocations
 * - Formats approval results for backend consumption
 */

interface BackendMessage {
  role: "user" | "assistant" | "system";
  content: string;
  toolInvocations?: BackendToolInvocation[];
}

interface BackendToolInvocation {
  toolCallId: string;
  toolName: string;
  state: "result";
  args: any;
  result: {
    approved: boolean;
    approvalId: string;
  };
}

/**
 * Transform AI SDK v6 messages to backend schema format
 *
 * @param messages - AI SDK v6 messages with parts array
 * @returns Backend-compatible messages with toolInvocations
 */
export function adaptMessagesForBackend(messages: any[]): BackendMessage[] {
  const result = messages.map((msg, index) => {
    // User messages: extract content from parts array
    if (msg.role === "user") {
      return {
        role: "user" as const,
        content: extractTextContent(msg.parts),
      };
    }

    // Assistant messages: extract content and tool invocations
    if (msg.role === "assistant") {
      const content = extractTextContent(msg.parts);
      const toolInvocations = extractToolInvocations(msg.parts);

      // If there's no text content but there are tool invocations, provide a fallback
      // The backend requires non-empty text content, so we use a space character
      const finalContent = content || (toolInvocations.length > 0 ? " " : "");

      return {
        role: "assistant" as const,
        content: finalContent,
        ...(toolInvocations.length > 0 && { toolInvocations }),
      };
    }

    // System messages (rare): extract from parts if available, fallback to content
    if (msg.role === "system") {
      // System messages are rare, only log if there's an issue
      if (
        process.env.NODE_ENV === "development" &&
        !msg.content &&
        !msg.parts
      ) {
        console.warn("[messageAdapter] Empty system message detected:", msg.id);
      }
    }

    return {
      role: msg.role as "user" | "assistant" | "system",
      content: msg.parts ? extractTextContent(msg.parts) : msg.content || "",
    };
  });

  return result;
}

/**
 * Extract text content from AI SDK v6 parts array
 */
function extractTextContent(parts: any[] | undefined): string {
  if (!parts) return "";

  const textParts = parts
    .filter((part) => part.type === "text" && part.text)
    .map((part) => part.text);

  return textParts.join("");
}

/**
 * Extract tool invocations from AI SDK v6 parts array
 *
 * Only includes tools in "approval-responded" state (user approved/denied)
 * Formats them as backend expects: { toolCallId, toolName, state: "result", args, result }
 */
function extractToolInvocations(
  parts: any[] | undefined
): BackendToolInvocation[] {
  if (!parts) return [];

  const toolInvocations: BackendToolInvocation[] = [];

  for (const part of parts) {
    // Look for tool parts that have been responded to (approved/denied)
    if (
      part.type?.startsWith("tool-") &&
      part.state === "approval-responded" &&
      part.approval
    ) {
      const toolName = part.type.replace("tool-", "");

      toolInvocations.push({
        toolCallId: part.toolCallId,
        toolName,
        state: "result", // Backend expects "result" for approval responses
        args: part.input || {}, // Original tool arguments
        result: {
          approved: part.approval.approved,
          approvalId: part.approval.id,
        },
      });
    }
  }

  return toolInvocations;
}
